/**
 * API Client Service
 * Handles all API requests to the backend
 */

import axios, { AxiosInstance } from 'axios';
import type { UserProfile, OpenPositionParams, OpenPositionResult, SettlePositionResult, BtcKline } from '@trading-sim/shared';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

class ApiClient {
  private client: AxiosInstance;
  private authErrorCallback?: () => void;

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token to requests
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle errors with automatic token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          console.warn('🔒 Received 401 - token invalid or expired');
          console.warn('🔒 Request URL:', error.config?.url);
          console.warn('🔒 Request method:', error.config?.method);
          
          // Clear invalid token
          localStorage.removeItem('auth_token');
          
          // Notify auth system about token expiration
          if (this.authErrorCallback) {
            console.warn('🔒 Calling auth error callback');
            this.authErrorCallback();
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Set callback for auth errors
  setAuthErrorCallback(callback: () => void) {
    console.log('🔧 Setting auth error callback');
    this.authErrorCallback = callback;
  }

  // Clear auth error callback
  clearAuthErrorCallback() {
    console.log('🔧 Clearing auth error callback');
    this.authErrorCallback = undefined;
  }

  // Auth
  async validateAuth(initData: string) {
    return this.client.post('/api/v1/auth/validate', { initData });
  }

  // User
  async getProfile(): Promise<{ data: UserProfile }> {
    return this.client.get('/api/v1/users/me');
  }

  async updateSettings(settings: { language?: 'ru' | 'en' | 'tr' }) {
    return this.client.patch('/api/v1/users/me', settings);
  }

  // Trades — Prediction Market
  async getBtcPrice(): Promise<{ data: { price: number } }> {
    return this.client.get('/api/v1/trades/btc-price');
  }

  async getBtcKlines(interval = '1m', limit = 60): Promise<{ data: { klines: BtcKline[] } }> {
    return this.client.get('/api/v1/trades/btc-klines', { params: { interval, limit } });
  }

  async openPosition(params: OpenPositionParams): Promise<{ data: OpenPositionResult }> {
    return this.client.post('/api/v1/trades/open', params);
  }

  async settlePosition(tradeId: string): Promise<{ data: SettlePositionResult }> {
    return this.client.post('/api/v1/trades/settle', { tradeId });
  }

  async getActivePosition() {
    return this.client.get('/api/v1/trades/active');
  }

  async getTradeHistory(limit = 50, offset = 0) {
    return this.client.get('/api/v1/trades/history', { params: { limit, offset } });
  }

  // Assets
  async getAssetStore() {
    return this.client.get('/api/v1/assets/store');
  }

  async getPortfolio() {
    return this.client.get('/api/v1/assets/portfolio');
  }

  async purchaseAsset(assetId: string, currency: 'witon' | 'usdt' | 'stars') {
    return this.client.post('/api/v1/assets/purchase', { assetId, currency });
  }

  // Stats
  async getStats(period: 'week' | 'month' | 'year' | 'all' = 'all') {
    return this.client.get('/api/v1/stats', { params: { period } });
  }

  // Leaderboard
  async getLeaderboard(
    metric: 'balance' | 'income' | 'winRate' = 'balance',
    period: 'week' | 'month' | 'year' | 'all' = 'all',
    limit = 100
  ) {
    return this.client.get('/api/v1/leaderboard', { params: { metric, period, limit } });
  }

  async getLeaderboardStats() {
    return this.client.get('/api/v1/leaderboard/stats');
  }

  // Payments
  async createStarsInvoice(params: {
    type: 'coins' | 'asset' | 'vip';
    itemId?: string;
    starsAmount: number;
  }) {
    return this.client.post('/api/v1/payments/stars/invoice', params);
  }

  async getCoinPackages() {
    return this.client.get('/api/v1/payments/packages');
  }

  // Subscription
  async getVipOptions() {
    return this.client.get('/api/v1/subscription/options');
  }

  async getVipStatus() {
    return this.client.get('/api/v1/subscription/status');
  }

  async purchaseVip(starsAmount: number) {
    return this.client.post('/api/v1/subscription/purchase', { starsAmount });
  }

  // Referral
  async getReferralLink() {
    return this.client.get('/api/v1/referral/link');
  }

  async getReferralStats() {
    return this.client.get('/api/v1/referral/stats');
  }

  async getReferredUsers(limit = 50, offset = 0) {
    return this.client.get('/api/v1/referral/users', { params: { limit, offset } });
  }

  async getReferralEarnings(limit = 50) {
    return this.client.get('/api/v1/referral/earnings', { params: { limit } });
  }

  async getReferralTree() {
    return this.client.get('/api/v1/referral/tree');
  }

  async getReferralList(page = 1, limit = 10) {
    return this.client.get('/api/v1/referral/list', { params: { page, limit } });
  }

  async searchReferrals(q: string) {
    return this.client.get('/api/v1/referral/search', { params: { q } });
  }

  async getReferralSponsor() {
    return this.client.get('/api/v1/referral/sponsor');
  }

  // Transfer
  async sendTransfer(recipientUsername: string, amount: number) {
    return this.client.post('/api/v1/transfers/send', { recipientUsername, amount });
  }

  async getTransferHistory(page = 1, limit = 20) {
    return this.client.get('/api/v1/transfers/history', { params: { page, limit } });
  }

  async searchUsersForTransfer(q: string) {
    return this.client.get('/api/v1/transfers/search-users', { params: { q } });
  }

  async getUserTransactions(page = 1, limit = 20, type?: string) {
    return this.client.get('/api/v1/users/transactions', { params: { page, limit, type } });
  }

  async getPlatformStats() {
    return this.client.get('/api/v1/leaderboard/platform-stats');
  }

  // Swap
  async getSwapRate() {
    return this.client.get('/api/v1/swap/rate');
  }

  async swapWitonToUsdt(amount: number) {
    return this.client.post('/api/v1/swap/witon-to-usdt', { amount });
  }

  async swapUsdtToWiton(amount: number) {
    return this.client.post('/api/v1/swap/usdt-to-witon', { amount });
  }

  // Withdrawal
  async getWithdrawalConfig() {
    return this.client.get('/api/v1/withdrawal/config');
  }

  async requestWithdrawal(amount: number, wallet: string, memo?: string) {
    return this.client.post('/api/v1/withdrawal/request', { amount, wallet, memo });
  }

  async getWithdrawalHistory() {
    return this.client.get('/api/v1/withdrawal/history');
  }

  // Deposits
  async getBalanceDepositMemo() {
    return this.client.get('/api/v1/deposits/balance/memo');
  }

  async createPurchaseOrder(assetId: string) {
    return this.client.post('/api/v1/deposits/orders', { assetId });
  }

  async getPurchaseOrder(orderId: string) {
    return this.client.get(`/api/v1/deposits/orders/${orderId}`);
  }

  async getDepositHistory() {
    return this.client.get('/api/v1/deposits/history');
  }

  async getPendingOrders() {
    return this.client.get('/api/v1/deposits/orders');
  }

  // Tasks endpoints
  async getTasks() {
    return this.client.get('/api/v1/tasks');
  }

  async getUserTasks() {
    return this.client.get('/api/v1/tasks');
  }

  async completeTask(taskId: string) {
    return this.client.post(`/api/v1/tasks/${taskId}/complete`);
  }

  // Mining endpoints
  async getMiningStatus() {
    return this.client.get('/api/v1/mining/status');
  }

  async claimMining() {
    return this.client.post('/api/v1/mining/claim');
  }

  // Daily reward endpoints
  async getDailyRewardStatus() {
    return this.client.get('/api/v1/daily-reward/status');
  }

  async claimDailyReward() {
    return this.client.post('/api/v1/daily-reward/claim');
  }

  // Battery endpoints
  async getBatteryStatus() {
    return this.client.get('/api/v1/battery/status');
  }

  async chargeBattery() {
    return this.client.post('/api/v1/battery/charge');
  }

  async startBatteryCycle(tier: number = 1) {
    return this.client.post('/api/v1/battery/start-cycle', { tier });
  }

  async withdrawBatteryEarnings() {
    return this.client.post('/api/v1/battery/withdraw');
  }

  async upgradeBattery() {
    return this.client.post('/api/v1/battery/upgrade');
  }

  // Staking endpoints
  async getStakingStatus() {
    return this.client.get('/api/v1/staking/status');
  }

  async getStakingConfig() {
    return this.client.get('/api/v1/staking/config');
  }

  async stakingDeposit(amount: number, packageMonths: number) {
    return this.client.post('/api/v1/staking/deposit', { amount, packageMonths });
  }

  async stakingClaim() {
    return this.client.post('/api/v1/staking/claim');
  }

  async stakingTransferToMain(amount: number) {
    return this.client.post('/api/v1/staking/transfer-to-main', { amount });
  }

  async stakingWithdraw(amount: number, wallet: string) {
    return this.client.post('/api/v1/staking/withdraw', { amount, wallet });
  }

  // USDT Staking endpoints
  async getUsdtStakingStatus() {
    return this.client.get('/api/v1/usdt-staking/status');
  }

  async getUsdtStakingConfig() {
    return this.client.get('/api/v1/usdt-staking/config');
  }

  async usdtStakingDeposit(amount: number, packageMonths: number) {
    return this.client.post('/api/v1/usdt-staking/deposit', { amount, packageMonths });
  }

  async usdtStakingClaim() {
    return this.client.post('/api/v1/usdt-staking/claim');
  }

  async usdtStakingTransferToMain(amount: number) {
    return this.client.post('/api/v1/usdt-staking/transfer-to-main', { amount });
  }

  async usdtStakingWithdraw(amount: number, wallet: string) {
    return this.client.post('/api/v1/usdt-staking/withdraw', { amount, wallet });
  }

  // Admin endpoints
  async getAdminDashboard() {
    return this.client.get('/api/v1/admin/dashboard');
  }

  async triggerIncome() {
    return this.client.post('/api/v1/admin/income/trigger');
  }

  async triggerDeposits() {
    return this.client.post('/api/v1/admin/deposits/trigger');
  }

  async getAdminUsers(params?: { page?: number; limit?: number; search?: string }) {
    return this.client.get('/api/v1/admin/users', { params });
  }

  async getAdminTrades(params?: { page?: number; limit?: number; result?: string; userId?: string }) {
    return this.client.get('/api/v1/admin/trades', { params });
  }

  async getAdminTradeStats() {
    return this.client.get('/api/v1/admin/trades/stats');
  }

  async getAdminAssets() {
    return this.client.get('/api/v1/admin/assets');
  }

  async getAdminDeposits(params?: { page?: number; limit?: number; status?: string; search?: string }) {
    return this.client.get('/api/v1/admin/deposits', { params });
  }

  async getAdminDepositsStats() {
    return this.client.get('/api/v1/admin/deposits/stats');
  }

  async getAdminBatteries(status?: string) {
    return this.client.get('/api/v1/admin/batteries', { params: { status } });
  }

  async getAdminWithdrawals(status?: string) {
    return this.client.get('/api/v1/admin/withdrawals', { params: { status } });
  }

  async approveWithdrawal(id: string, txHash?: string, reason?: string) {
    return this.client.patch(`/api/v1/admin/withdrawals/${id}/approve`, { txHash, reason });
  }

  async rejectWithdrawal(id: string, reason: string) {
    return this.client.patch(`/api/v1/admin/withdrawals/${id}/reject`, { reason });
  }

  async getAdminPurchases(params?: { page?: number; limit?: number; userId?: string }) {
    return this.client.get('/api/v1/admin/purchases', { params });
  }

  async sendBroadcast(message: string, targetUsers?: 'all' | 'vip' | 'active' | number[]) {
    return this.client.post('/api/v1/admin/broadcast', { message, targetUsers });
  }

  async modifyUserBalance(userId: string, amount: number, reason: string) {
    return this.client.patch(`/api/v1/admin/users/${userId}/balance`, { amount, reason });
  }

  async modifyUserUsdtBalance(userId: string, amount: number, reason: string) {
    return this.client.patch(`/api/v1/admin/users/${userId}/usdt-balance`, { amount, reason });
  }

  async freezeUser(userId: string, frozen: boolean, reason?: string) {
    return this.client.post(`/api/v1/admin/users/${userId}/freeze`, { frozen, reason });
  }

  async freezeUserMining(userId: string, frozen: boolean) {
    return this.client.post(`/api/v1/admin/users/${userId}/mining-freeze`, { frozen });
  }

  async getAdminTransfers(params?: { page?: number; limit?: number }) {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    return this.client.get(`/api/v1/admin/transfers?${query}`);
  }

  // Admin tasks endpoints
  async getAdminTasks() {
    return this.client.get('/api/v1/admin/tasks');
  }

  async uploadImage(file: File): Promise<{ url: string; publicId: string; format: string; size: number }> {
    try {
      console.log('[API] Uploading image:', file.name);
      
      // Конвертация файла в base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (typeof reader.result === 'string') {
            resolve(reader.result);
          } else {
            reject(new Error('Failed to read file as base64'));
          }
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });

      // Отправка на сервер
      const response = await this.client.post('/api/v1/admin/upload/image', {
        image: base64,
        mimeType: file.type,
        fileName: file.name,
      });

      if (response && response.data && response.data.success && response.data.data) {
        console.log('[API] Image uploaded successfully:', response.data.data.url);
        return response.data.data;
      }

      throw new Error(response.data?.error?.message || 'Upload failed');
    } catch (error) {
      console.error('[API] uploadImage error:', error);
      throw new Error(`Upload image error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async createTask(params: { 
    title: string; 
    description: string; 
    url: string; 
    reward: number; 
    icon?: string; 
  }) {
    return this.client.post('/api/v1/admin/tasks', params);
  }

  async updateTask(taskId: string, params: any) {
    return this.client.put(`/api/v1/admin/tasks/${taskId}`, params);
  }

  async deleteTask(taskId: string) {
    return this.client.delete(`/api/v1/admin/tasks/${taskId}`);
  }

  async getTaskStats() {
    return this.client.get('/api/v1/admin/tasks/stats');
  }

  // Admin staking endpoints
  async getAdminStakingDeposits(status?: string) {
    return this.client.get('/api/v1/admin/staking/deposits', { params: { status } });
  }

  async getAdminStakingStats() {
    return this.client.get('/api/v1/admin/staking/stats');
  }

  async updateStakingConfig(key: string, value: string) {
    return this.client.patch('/api/v1/admin/staking/config', { key, value });
  }

  async modifyUserStakingBalance(userId: string, amount: number, reason: string) {
    return this.client.patch(`/api/v1/admin/users/${userId}/staking-balance`, { amount, reason });
  }

  async triggerStakingExpired() {
    return this.client.post('/api/v1/admin/staking/complete-expired');
  }

  // Admin USDT Staking
  async getAdminUsdtStakingDeposits(status?: string) {
    return this.client.get('/api/v1/admin/usdt-staking/deposits', { params: { status } });
  }

  async getAdminUsdtStakingStats() {
    return this.client.get('/api/v1/admin/usdt-staking/stats');
  }

  async modifyUserUsdtStakingBalance(userId: string, amount: number, reason: string) {
    return this.client.patch(`/api/v1/admin/users/${userId}/usdt-staking-balance`, { amount, reason });
  }

  // AI Trader
  async getAiTraderStatus() {
    return this.client.get('/api/v1/ai-trader/status');
  }
  async getAiTraderPackages() {
    return this.client.get('/api/v1/ai-trader/packages');
  }
  async purchaseAiTraderBot(packageKey: string) {
    return this.client.post('/api/v1/ai-trader/purchase', { packageKey });
  }
  async upgradeAiTraderBot(packageKey: string) {
    return this.client.post('/api/v1/ai-trader/upgrade', { packageKey });
  }
  async catchAiTraderOpportunity(opportunityId: string) {
    return this.client.post('/api/v1/ai-trader/catch', { opportunityId });
  }
  async transferAiTraderReward(opportunityId: string) {
    return this.client.post('/api/v1/ai-trader/transfer-reward', { opportunityId });
  }
  async acknowledgeAiTraderLock() {
    return this.client.post('/api/v1/ai-trader/acknowledge-lock');
  }
  async resumeAiTraderBot() {
    return this.client.post('/api/v1/ai-trader/resume');
  }
  async sendAiTraderGift(username: string) {
    return this.client.post('/api/v1/ai-trader/gift', { username });
  }
  async transferBotBalanceToMain(amount: number) {
    return this.client.post('/api/v1/ai-trader/transfer-to-main', { amount });
  }
  async getAiTraderOpportunities() {
    return this.client.get('/api/v1/ai-trader/opportunities');
  }
  async getAiTraderGifts() {
    return this.client.get('/api/v1/ai-trader/gifts');
  }
  async getAiTraderDepositInfo() {
    return this.client.get('/api/v1/ai-trader/deposit-info');
  }
  async getAiTraderHistory() {
    return this.client.get('/api/v1/ai-trader/history');
  }
  async lookupAiTraderGiftRecipient(username: string) {
    return this.client.get('/api/v1/ai-trader/gift-recipient', { params: { username } });
  }

  // Admin AI Trader
  async getAdminAiTraderBots(status?: string) {
    return this.client.get('/api/v1/admin/ai-trader/bots', { params: { status } });
  }
  async getAdminAiTraderStats() {
    return this.client.get('/api/v1/admin/ai-trader/stats');
  }
  async getAdminAiTraderOpportunities(botId?: string, status?: string) {
    return this.client.get('/api/v1/admin/ai-trader/opportunities', { params: { botId, status } });
  }
  async addAiTraderSignal(opportunityId: string, signal: { pair: string; direction: string; entry: string; tp: string; sl: string; exchange: string }) {
    return this.client.post('/api/v1/admin/ai-trader/signal', { opportunityId, ...signal });
  }
  async modifyUserBotBalance(userIdentifier: string, amount: number, reason: string) {
    return this.client.patch(`/api/v1/admin/users/${encodeURIComponent(userIdentifier.trim())}/bot-balance`, { amount, reason });
  }
}

export const api = new ApiClient();
